import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, ContentChild, DestroyRef, DOCUMENT, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { Event, NavigationEnd, Router, Scroll } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, startWith, withLatestFrom } from 'rxjs/operators';

import { LAYOUT } from '../../layout.constants';
import { LayoutService } from '../../services/layout.service';
import { checkRouterChildsData } from '../../utils/check-router-childs-data';
import { isNil } from '../../utils/is-nil';

/**
 * Container the shell's `<mat-sidenav-container>` is projected into. It owns the body-level layout
 * classes and the router scroll handling, which has to drive `<mat-sidenav-content>` rather than the
 * document because the document itself does not scroll (see styles/_shell.scss).
 */
@Component({
  selector: 'rms-base-layout',
  templateUrl: './base-layout.component.html',
  imports: [MatSidenavModule, AsyncPipe],
})
export class BaseLayoutComponent implements OnInit, AfterViewInit {
  public readonly isFooterVisible$: Observable<boolean>;
  public readonly scrollDisabled$: Observable<boolean>;
  public readonly sidenavCollapsed$: Observable<boolean>;
  public readonly isDesktop$: Observable<boolean>;

  @ContentChild(MatSidenavContainer, { static: true })
  public sidenavContainer!: MatSidenavContainer;

  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sidenavCollapsed$ = this.layoutService.sidenavCollapsed$;
    this.isDesktop$ = this.layoutService.isDesktop$;

    this.isFooterVisible$ = this.onNavigationEnd().pipe(
      map(() => checkRouterChildsData(this.router.routerState.root.snapshot, (data) => data.footerVisible ?? true)),
      map((routeEnabled) => (isNil(routeEnabled) ? LAYOUT.footerVisible : LAYOUT.footerVisible && routeEnabled))
    );

    this.scrollDisabled$ = this.onNavigationEnd().pipe(
      map(() => checkRouterChildsData(this.router.routerState.root.snapshot, (data) => data.scrollDisabled ?? false))
    );
  }

  ngOnInit(): void {
    /**
     * Open the sidenav as a rail on desktop, keep it closed (as an overlay) below that.
     */
    this.isDesktop$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isDesktop) => {
      if (isDesktop) {
        this.layoutService.openSidenav();
      } else {
        this.layoutService.closeSidenav();
      }
    });

    /**
     * Mobile only: close the sidenav after navigating somewhere, e.g. when a user clicks a link in it.
     */
    this.onNavigationEnd()
      .pipe(
        withLatestFrom(this.isDesktop$),
        filter(([, isDesktop]) => !isDesktop),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.layoutService.closeSidenav());
  }

  ngAfterViewInit(): void {
    /**
     * Enable scrolling to specific parts of the page using the router.
     */
    this.router.events
      .pipe(
        filter<Event, Scroll>((event: Event): event is Scroll => event instanceof Scroll),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        if (event.position) {
          // backward navigation
          this.sidenavContainer.scrollable.scrollTo({
            start: event.position[0],
            top: event.position[1],
          });
        } else if (event.anchor) {
          this.scrollToAnchor(event.anchor);
        } else {
          // forward navigation
          this.sidenavContainer.scrollable.scrollTo({
            top: 0,
            start: 0,
          });
        }
      });
  }

  private scrollToAnchor(anchor: string): void {
    const scroll = (element: HTMLElement): void =>
      this.sidenavContainer.scrollable.scrollTo({
        behavior: 'smooth',
        top: element.offsetTop,
        left: element.offsetLeft,
      });

    const anchorElem = this.document.getElementById(anchor);

    if (anchorElem) {
      scroll(anchorElem);

      return;
    }

    // The target may not be rendered yet on the navigation we are handling.
    setTimeout(() => {
      const delayedElem = this.document.getElementById(anchor);

      if (delayedElem) {
        scroll(delayedElem);
      }
    }, 100);
  }

  private onNavigationEnd(): Observable<NavigationEnd | null> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null)
    );
  }
}
